/**
 * fieldKeyUtils.ts
 *
 * Shared helpers for identifying field-key categories used across charts code.
 */

export const IOA_PREFIXES = ['input.', 'output.', 'attributes.'] as const;
export const FEEDBACK_PREFIXES = [
  'annotations.',
  'scores.',
  'reactions.',
] as const;

export type FieldCategory =
  | 'input'
  | 'output'
  | 'attributes'
  | 'annotations'
  | 'scores'
  | 'reactions'
  | 'other';

/**
 * Determines if a key targets inputs/outputs/attributes.
 *
 * Args:
 *   key (string): The field key.
 *
 * Returns:
 *   boolean: True if the key starts with one of IOA prefixes.
 *
 * Examples:
 *   isIOAField("input.model") // true
 *   isIOAField("annotations.Quality") // false
 */
export function isIOAField(key: string): boolean {
  return IOA_PREFIXES.some(prefix => key.startsWith(prefix));
}

/**
 * Determines if a key targets feedback (annotations/scores/reactions).
 *
 * Args:
 *   key (string): The field key.
 *
 * Returns:
 *   boolean: True if the key starts with a feedback prefix.
 *
 * Examples:
 *   isFeedbackField("scores.toxicity") // true
 *   isFeedbackField("output.response") // false
 */
export function isFeedbackField(key: string): boolean {
  return FEEDBACK_PREFIXES.some(prefix => key.startsWith(prefix));
}

/**
 * Human-friendly labels for IOA prefixes.
 */
export const IOA_SOURCE_LABELS: Record<
  'input' | 'output' | 'attributes',
  string
> = {
  input: 'Input',
  output: 'Output',
  attributes: 'Attribute',
};
